import React, { useState } from 'react';
import { Link, Navigate} from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from '../components/auth/AuthLayout';

const Register = () => {
  return <Navigate to="/" replace />; // redirect immediately
  const { register: registerUser, loading } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selfieFile, setSelfieFile] = useState(null);
  const [idFile, setIdFile] = useState(null);
  
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
    clearErrors
  } = useForm();

  const password = watch('password');

  const onSelfieDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setSelfieFile(acceptedFiles[0]);
    }
  };

  const onIdDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setIdFile(acceptedFiles[0]);
    }
  };

  const { getRootProps: getSelfieRootProps, getInputProps: getSelfieInputProps, isDragActive: isSelfieDragActive } = useDropzone({
    onDrop: onSelfieDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png']
    },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024 // 5MB
  });

  const { getRootProps: getIdRootProps, getInputProps: getIdInputProps, isDragActive: isIdDragActive } = useDropzone({
    onDrop: onIdDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png'],
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024 // 10MB
  });

  const onSubmit = async (data) => {
    clearErrors();
    
    // Validate files
    if (!selfieFile) {
      setError('selfie', {
        type: 'manual',
        message: 'Selfie photo is required'
      });
      return;
    }

    if (!idFile) {
      setError('idDocument', {
        type: 'manual',
        message: 'Government ID is required'
      });
      return;
    }

    // Create FormData for file upload
    const formData = new FormData();
    formData.append('firstName', data.firstName);
    formData.append('lastName', data.lastName);
    formData.append('email', data.email);
    formData.append('password', data.password);
    formData.append('phone', data.phone);
    formData.append('dateOfBirth', data.dateOfBirth);
    formData.append('selfie', selfieFile);
    formData.append('idDocument', idFile);

    const result = await registerUser(formData);
    
    if (!result.success) {
      setError('root', {
        type: 'manual',
        message: result.error
      });
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <AuthLayout 
      title="CREATE AN ACCOUNT" 
      subtitle="Join PROSKINS and start betting on golf tournaments"
    >
      {errors.root && (
        <div className="proskins-alert proskins-alert-error">
          <span>❌</span>
          <div>
            <strong>Registration failed</strong>
            <p>{errors.root.message}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
        <div className="auth-form-row">
          <div className="proskins-form-group">
            <label className="proskins-label">First Name</label>
            <input
              type="text"
              className={`proskins-input ${errors.firstName ? 'proskins-input-error' : ''}`}
              placeholder="Enter your first name"
              {...register('firstName', {
                required: 'First name is required',
                minLength: {
                  value: 2,
                  message: 'First name must be at least 2 characters'
                }
              })}
            />
            {errors.firstName && (
              <div className="proskins-text-accent" style={{ fontSize: 'var(--font-size-xs)', marginTop: 'var(--spacing-xs)' }}>
                {errors.firstName.message}
              </div>
            )}
          </div>

          <div className="proskins-form-group">
            <label className="proskins-label">Last Name</label>
            <input
              type="text"
              className={`proskins-input ${errors.lastName ? 'proskins-input-error' : ''}`}
              placeholder="Enter your last name"
              {...register('lastName', {
                required: 'Last name is required',
                minLength: {
                  value: 2,
                  message: 'Last name must be at least 2 characters'
                }
              })}
            />
            {errors.lastName && (
              <div className="proskins-text-accent" style={{ fontSize: 'var(--font-size-xs)', marginTop: 'var(--spacing-xs)' }}>
                {errors.lastName.message}
              </div>
            )}
          </div>
        </div>

        <div className="auth-form-row">
          <div className="proskins-form-group">
            <label className="proskins-label">Phone Number</label>
            <input
              type="tel"
              className={`proskins-input ${errors.phone ? 'proskins-input-error' : ''}`}
              placeholder="Enter your phone number"
              {...register('phone', {
                required: 'Phone number is required',
                pattern: {
                  value: /^[\+]?[1-9][\d]{0,15}$/,
                  message: 'Please enter a valid phone number'
                }
              })}
            />
            {errors.phone && (
              <div className="proskins-text-accent" style={{ fontSize: 'var(--font-size-xs)', marginTop: 'var(--spacing-xs)' }}>
                {errors.phone.message}
              </div>
            )}
          </div>

          <div className="proskins-form-group">
            <label className="proskins-label">Email</label>
            <input
              type="email"
              className={`proskins-input ${errors.email ? 'proskins-input-error' : ''}`}
              placeholder="Enter your email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Please enter a valid email address'
                }
              })}
            />
            {errors.email && (
              <div className="proskins-text-accent" style={{ fontSize: 'var(--font-size-xs)', marginTop: 'var(--spacing-xs)' }}>
                {errors.email.message}
              </div>
            )}
          </div>
        </div>

        <div className="proskins-form-group">
          <label className="proskins-label">Address 1</label>
          <input
            type="text"
            className="proskins-input"
            placeholder="Enter your address"
          />
        </div>

        <div className="proskins-form-group">
          <label className="proskins-label">Address 2</label>
          <input
            type="text"
            className="proskins-input"
            placeholder="Apartment, suite, etc. (optional)"
          />
        </div>

        <div className="auth-form-row-3">
          <div className="proskins-form-group">
            <label className="proskins-label">City</label>
            <input
              type="text"
              className="proskins-input"
              placeholder="City"
            />
          </div>

          <div className="proskins-form-group">
            <label className="proskins-label">State</label>
            <input
              type="text"
              className="proskins-input"
              placeholder="State"
            />
          </div>

          <div className="proskins-form-group">
            <label className="proskins-label">Zip</label>
            <input
              type="text"
              className="proskins-input"
              placeholder="ZIP Code"
            />
          </div>
        </div>

        <div className="auth-form-row">
          <div className="proskins-form-group">
            <label className="proskins-label">Password</label>
            <div className="auth-password-container">
              <input
                type={showPassword ? 'text' : 'password'}
                className={`proskins-input ${errors.password ? 'proskins-input-error' : ''}`}
                placeholder="Create a strong password"
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters'
                  },
                  pattern: {
                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                    message: 'Password must contain uppercase, lowercase, number and special character'
                  }
                })}
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '👁️‍🗨️' : '👁️'}
              </button>
            </div>
            {errors.password && (
              <div className="proskins-text-accent" style={{ fontSize: 'var(--font-size-xs)', marginTop: 'var(--spacing-xs)' }}>
                {errors.password.message}
              </div>
            )}
          </div>

          <div className="proskins-form-group">
            <label className="proskins-label">Password Confirm</label>
            <div className="auth-password-container">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                className={`proskins-input ${errors.confirmPassword ? 'proskins-input-error' : ''}`}
                placeholder="Confirm your password"
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: (value) => value === password || 'Passwords do not match'
                })}
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={toggleConfirmPasswordVisibility}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? '👁️‍🗨️' : '👁️'}
              </button>
            </div>
            {errors.confirmPassword && (
              <div className="proskins-text-accent" style={{ fontSize: 'var(--font-size-xs)', marginTop: 'var(--spacing-xs)' }}>
                {errors.confirmPassword.message}
              </div>
            )}
          </div>
        </div>

        <div className="auth-form-actions">
          <button
            type="button"
            className="proskins-btn proskins-btn-secondary"
            onClick={() => document.getElementById('license-upload').click()}
          >
            <span>+</span>
            Upload License
          </button>
          <input
            id="license-upload"
            type="file"
            accept="image/*,.pdf"
            style={{ display: 'none' }}
            onChange={(e) => setIdFile(e.target.files[0])}
          />

          <button
            type="button"
            className="proskins-btn proskins-btn-secondary"
            onClick={() => document.getElementById('photo-upload').click()}
          >
            <span>+</span>
            Upload Photo
          </button>
          <input
            id="photo-upload"
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => setSelfieFile(e.target.files[0])}
          />
        </div>

        <button
          type="submit"
          className="proskins-btn proskins-btn-primary proskins-btn-large proskins-btn-full"
          disabled={loading}
        >
          {loading ? (
            <>
              <div className="proskins-loading"></div>
              Creating Account...
            </>
          ) : (
            'CREATE ACCOUNT'
          )}
        </button>
      </form>

      <div className="auth-divider">
        <span>or</span>
      </div>

      <div className="auth-text">
        Already have an account?{' '}
        <Link to="/login" className="auth-link">
          Sign in here
        </Link>
      </div>
    </AuthLayout>
  );
};

export default Register;
